import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip during Google OAuth callback (handled separately)
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("frenchies_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("frenchies_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const applyAuth = useCallback((data) => {
    localStorage.setItem("frenchies_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => applyAuth((await api.post("/auth/login", { email, password })).data), [applyAuth]);
  const register = useCallback(async (payload) => applyAuth((await api.post("/auth/register", payload)).data), [applyAuth]);
  const googleSession = useCallback(async (session_id) => applyAuth((await api.post("/auth/google-session", { session_id })).data), [applyAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("Logout request failed (ignored)", e); }
    localStorage.removeItem("frenchies_token");
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) { console.debug("refreshUser failed (ignored)", e); }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, googleSession, logout, refreshUser, setUser }),
    [user, loading, login, register, googleSession, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
